<template>
  <MainLayout>
    <v-layout style="padding: 1em" justify-center row fill-height>
      <v-flex>
        <v-card style="height: 100%" light color="#f4f4fa">
          <v-layout column style="height: 100%">
            <!-- Title -->
            <v-card-title row class="align-center">
              <span class="headline flex-none">Vendors</span>
            </v-card-title>

            <!-- Filter -->
            <div class="d-flex flex-xs-column justify-end align-center">
              <!-- Search Field -->
              <v-text-field
                class="mx-4"
                v-model="filters.search"
                prepend-icon="search"
                label="Search by name, mobile, email, skill"
                clearable
                @input="getVendors"
              />

              <!-- Filter Date Range -->
              <div class="flex-none d-flex mx-4">
                <DatePicker
                  v-model="filters.fromDate"
                  clearable
                  label="From Date"
                  @change="getVendors"
                />

                <DatePicker
                  v-model="filters.toDate"
                  clearable
                  label="To Date"
                  @change="getVendors"
                />
              </div>
            </div>

            <!-- Table -->
            <v-container fluid fill-height class="full-height">
              <v-layout style="padding: 0" class="full-height">
                <v-flex sm12 md12 class="full-height scroll">
                  <v-data-table
                    :headers="headers"
                    :items="vendors"
                    :loading="loading"
                    :pagination.sync="pagination"
                    :rows-per-page-items="[10, 50, 100]"
                    :total-items="pagination.totalItems"
                    @update:pagination="getVendors"
                    class="elevation-1"
                  >
                    <!-- When items = [] -->
                    <template v-slot:no-data>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "No matching records found"
                        }}
                      </div>
                    </template>

                    <!-- When items = undefined -->
                    <template v-slot:no-results>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "Data will be displayed in here"
                        }}
                      </div>
                    </template>

                    <template v-slot:items="props">
                      <!-- Generated -->
                      <td>{{ props.item.created_at | date }}</td>

                      <!-- Registration ID -->
                      <td>{{ props.item.id }}</td>

                      <!-- Name -->
                      <td>{{ props.item.user.name }}</td>

                      <!-- Phone -->
                      <td>{{ props.item.user.mobile }}</td>

                      <!-- Email -->
                      <td>{{ props.item.user.email }}</td>

                      <!-- Skill Category -->
                      <td>
                        {{ props.item.service && props.item.service.name }}
                      </td>

                      <!-- Actions -->
                      <td v-if="typeof props.item.accepted === 'boolean'">
                        <v-btn
                          v-if="props.item.accepted"
                          flat
                          disabled
                          style="font-weight: 600; color: var(--success) !important"
                        >
                          Accepted
                        </v-btn>
                        <v-btn
                          v-else
                          flat
                          disabled
                          style="font-weight: 600; color: var(--danger) !important"
                        >
                          Rejected
                        </v-btn>
                      </td>
                      <td v-else>
                        <v-menu offset-y>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              :loading="
                                actionLoading && props.item === currentVendor
                              "
                              flat
                              style="font-weight: 600;"
                              v-on="on"
                            >
                              Pending <v-icon small>arrow_drop_down</v-icon>
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-tile
                              @click="confirmApproveUser(props.item)"
                            >
                              <v-list-tile-title>
                                <v-icon color="success" small>check</v-icon>
                                Accept
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile @click="confirmRejectUser(props.item)">
                              <v-list-tile-title>
                                <v-icon color="error" small>close</v-icon>
                                Reject
                              </v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </td>

                      <!-- Full Details -->
                      <td>
                        <v-btn
                          title="View Details"
                          icon
                          @click="viewVendorDetails(props.item)"
                        >
                          <v-icon small>visibility</v-icon>
                        </v-btn>
                      </td>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-container>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Vendor Details -->
    <v-dialog v-if="currentVendor" width="37.5em" v-model="vendorDetailsDialog">
      <v-flex light sm12 md12 class="full-height scroll">
        <!-- Header -->
        <v-toolbar color="primary" dark>
          <v-toolbar-title>
            Vendor Details
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="vendorDetailsDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Content -->
        <v-card light>
          <v-container fluid grid-list-lg>
            <v-layout column>
              <table class="vendor-details-table">
                <tbody>
                  <tr>
                    <td>Name:</td>
                    <td>{{ currentVendor.user.name }}</td>
                  </tr>

                  <tr>
                    <td>Phone:</td>
                    <td>{{ currentVendor.user.mobile }}</td>
                  </tr>

                  <tr>
                    <td>Email:</td>
                    <td>{{ currentVendor.user.email }}</td>
                  </tr>

                  <tr>
                    <td>Skill:</td>
                    <td>{{ currentVendor.service.name }}</td>
                  </tr>

                  <tr>
                    <td>Service Areas:</td>
                    <td>
                      {{ currentVendor.wards.map(w => w.name).join(", ") }}
                    </td>
                  </tr>

                  <tr>
                    <td>Business Name:</td>
                    <td>{{ currentVendor.business_name }}</td>
                  </tr>

                  <tr>
                    <td>Years of Exp.:</td>
                    <td>{{ currentVendor.years_of_experience }}</td>
                  </tr>

                  <tr>
                    <td>Working Days:</td>
                    <td>
                      {{
                        currentVendor.working_days
                          .split(";")
                          .filter(d => !!d)
                          .map(d => toFullDay(d))
                          .join(" - ")
                      }}
                    </td>
                  </tr>

                  <tr>
                    <td>Documents:</td>
                    <td>
                      <template v-if="currentVendor.documentations">
                        <a
                          v-for="(doc, i) of currentVendor.documentations"
                          :key="i"
                          :href="doc.path"
                          target="_blank"
                          style="margin-right: 0.625em"
                          >{{ doc.name }}</a
                        >
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-layout>
          </v-container>
        </v-card>
      </v-flex>
    </v-dialog>
  </MainLayout>
</template>

<script>
import moment from "moment";
import DatePicker from "@/components/DatePicker";
import sdk from "@/api/sdk";
import { debounce } from "@/utils/Debounce";

export default {
  metaInfo: {
    title: "Vendors",
    meta: [{ name: "Vendors", content: "vendors management" }]
  },

  components: {
    DatePicker
  },

  data() {
    return {
      currentVendor: null,
      vendorDetailsDialog: false,

      actionLoading: false,

      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: null,
        totalItems: 0
      },

      filters: {
        search: undefined,
        fromDate: undefined,
        toDate: undefined
      },

      loading: false,
      vendors: undefined,
      statuses: [],
      categories: [],

      headers: [
        {
          text: "Generated",
          width: 100,
          sortable: true,
          value: "created_at"
        },
        {
          text: "Registration ID",
          width: 50,
          sortable: true,
          value: "id"
        },
        {
          text: "Name",
          value: "user.name",
          sortable: false
        },
        {
          text: "Phone",
          value: "user.mobile",
          sortable: false
        },
        {
          text: "Email",
          value: "user.email",
          sortable: false
        },
        {
          text: "Skill Category",
          value: "service.name",
          sortable: false
        },
        {
          text: "Status",
          width: 100,
          sortable: false
        },
        {
          text: "Full Details",
          width: 50,
          sortable: false
        }
      ]
    };
  },

  mounted() {
    this.getVendors();
  },

  methods: {
    getVendors: debounce(async function() {
      try {
        this.loading = true;
        this.vendors = undefined;

        const res = await sdk.request({
          url: "/vendors",
          params: {
            sort_by: this.pagination.sortBy,
            sort_direction: this.pagination.descending ? "desc" : "asc",
            page_size: this.pagination.rowsPerPage,
            page: this.pagination.page,
            search: this.filters.search || undefined,
            start_time: this.filters.fromDate
              ? moment(this.filters.fromDate)
                  .startOf("day")
                  .format()
              : undefined,
            end_time: this.filters.toDate
              ? moment(this.filters.toDate)
                  .endOf("day")
                  .format()
              : undefined
          }
        });

        this.loading = false;
        this.vendors = res.data.data;
        this.pagination.totalItems = res.data.total;
      } finally {
        this.loading = false;
      }
    }, 500),

    async confirmApproveUser(vendor) {
      await this.$confirm("Do you want to approve this vendor?", "Info", {
        confirmButtonText: "Approve",
        cancelButtonText: "Cancel",
        type: "info"
      });

      this.approveUser(vendor);
    },

    async approveUser(vendor) {
      try {
        this.currentVendor = vendor;
        this.actionLoading = true;

        await sdk.request({
          method: "POST",
          url: `/vendors/${vendor.id}/accept`
        });

        this.$notify.success("User have been approve");

        vendor.accepted = true;
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.actionLoading = false;
        this.currentVendor = null;
      }
    },

    async confirmRejectUser(vendor) {
      await this.$confirm("Do you want to reject the vendor?", "Warning", {
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        type: "warning"
      });

      this.rejectUser(vendor);
    },

    async rejectUser(vendor) {
      try {
        this.currentVendor = vendor;
        this.actionLoading = true;

        await sdk.request({
          method: "POST",
          url: `/vendors/${vendor.id}/reject`
        });

        vendor.accepted = false;

        this.$notify.info("User have been rejected");
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.actionLoading = false;
        this.currentVendor = null;
      }
    },

    viewVendorDetails(vendor) {
      this.currentVendor = vendor;
      this.vendorDetailsDialog = true;
    },

    toFullDay(day) {
      switch (day) {
        case "Mon":
          return "Monday";
        case "Tue":
          return "Tuesday";
        case "Wed":
          return "Wednesday";
        case "Thu":
          return "Thursday";
        case "Fri":
          return "Friday";
        case "Sat":
          return "Saturday";
        case "Sun":
          return "Sunday";
        default:
          return undefined;
      }
    }
  }
};
</script>

<style lang="css" scoped>
.full-height {
  flex: 1 1 auto;
  min-height: 0;
}

.scroll {
  overflow-y: auto;
}

.title-text {
  flex: 1;
}

.search-field {
  flex: none;
  width: 25em;
}

.filter-select {
  flex: none;
  margin-left: 0.625em;
  width: 15em;
}

.vendor-details-table {
  border-spacing: 0 0.3125em;
}

.vendor-details-table > tbody > tr > td:first-child {
  white-space: nowrap;
  text-align: right;
  color: var(--secondary-text);
  vertical-align: text-top;
  font-weight: 600;
}

.vendor-details-table > tbody > tr > td:last-child {
  padding-left: 1.875em;
}
</style>
